import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import renderBlockHeader1 from "./blocks/renderBlockHeader1";
import renderBlockHeader2 from "./blocks/renderBlockHeader2";
import renderBlockText from "./blocks/renderBlockText";
import renderBlockHighlight from "./blocks/renderBlockHighlight";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { getUID } from "./../../utils";
import { Loader } from "../../stores/Loader";
import Button from "../Button";
import Switch from "../Switch";
import renderBlockPhoto from "./blocks/renderBlockPhoto";
import renderBlockVideo from "./blocks/renderBlockVideo";
import renderBlockAudio from "./blocks/renderBlockAudio";
import renderBlockBuiltInPDF from "./blocks/renderBlockBuiltInPDF";
import renderBlockHTMLEditor from "./blocks/renderBlockHTMLEditor";
import { Settings } from "../../stores/Settings";
import { getClasses } from "../../constants/theme";

const AccsessFileTypes = {
  "image/png": "photo",
  "image/jpeg": "photo",
  "video/mp4": "video",
};

const blocksTypes = {
  header1: renderBlockHeader1,
  header2: renderBlockHeader2,
  text: renderBlockText,
  highlight: renderBlockHighlight,
  photo: renderBlockPhoto,
  video: renderBlockVideo,
  audio: renderBlockAudio,
  builtInPDF: renderBlockBuiltInPDF,
  html: renderBlockHTMLEditor,
};

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  paddingTop: 0.1,
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({});

const MainLesonBlock = React.forwardRef(function (props, ref) {
  const formField = props.formField;
  const [blocks, setBlocks] = useState(props.form.link(formField, []));
  const sw = useRef(void 0);
  const sw2 = useRef(void 0);
  const swText = useRef(void 0);
  const swChange = useRef(void 0);
  const containerRef = useRef();
  const classes = getClasses(dynamicStyle);
  const [selected, setSelected] = useState(props.selected)

  const disabled = props.disabled;

  useImperativeHandle(ref, () => ({
    show: showDialog,
  }));

  useEffect(() => {
    if (props.emitter) {
      return props.emitter.on(formField, (b) => {
        setBlocks([...b]);
      });
    }
  }, [formField, props.emitter]);

  useEffect(() => {
    setSelected(props.selected)
  },[props.selected])

  const removeBlock = useCallback(
    (item) => {
      const b = props.form.get(formField, []).find((el) => el.id === item.id);

      if ((b.type === "photo" || b.type === "video") && b?.value?.tmpid) {
        Loader.delete(b?.value?.tmpid, b?.value?._id);
      }

      props.form.set(
        formField,
        props.form.get(formField, []).filter((el) => el.id !== item.id)
      );
    },
    [props.form, formField]
  );

  const showDialog = useCallback((item, e) => {
    sw.current && sw.current.show(item, e);
  }, []);

  const moveBlock = (block, direction) => {
    const _blocks = props.form.get(formField, []);
    const currentIndex = _blocks.findIndex((item) => item.id === block.id);
    _blocks.splice(currentIndex, 1);
    _blocks.splice(currentIndex + direction, 0, block);

    props.form.set(formField, [..._blocks]);
  };

  const changeBlockType = (item, e) => {
    swChange.current && swChange.current.show(item, e);
  };

  const changeType = (o, type, item) => {
    const _blocks = props.form.get(formField, []);

    props.form.set(formField, [
      ..._blocks.map((b) => {
        if (b.id === item.id) {
          b.type = type;
        }

        return b;
      }),
    ]);
  };

  const addBlock = useCallback(
    (item, type, defValue) => {
      const el = {
        id: getUID(),
        type: type,
        value: defValue,
      };
      const _blocks = props.form.get(formField, []);
      const index =
        item === void 0
          ? _blocks.length
          : _blocks.findIndex((block) => block.id === item.id) + 1;
      _blocks.splice(index, 0, el);

      props.form.set(formField, [..._blocks]);
    },
    [props.form, formField]
  );

  const saveValue = (id, value) => {
    const _blocks = props.form.get(formField);
    const index = _blocks.findIndex((block) => block.id === id);

    props.form.set(`${formField}.${index}.value`, value);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(
      blocks,
      result.source.index,
      result.destination.index
    );

    props.form.set(formField, [...items]);
  };

  const onDragStart = () => {};

  const availableBlocks = Settings.get("me.subscription.available_blocks", []);

  useEffect(() => {
    const onpaste = (evt) => {
      if (document.activeElement.tagName !== 'BODY' || !selected)  return;
      
      if (!(disabled !== true && props.showAddButton === true)) return;

      const dT = evt.clipboardData || window.clipboardData;
      const file = dT.files[0];

      if (file) {
        if (AccsessFileTypes[file.type]) {
          addBlock(void 0, AccsessFileTypes[file.type], file);
        } else {
          sw2.current && sw2.current.show(file, evt);
        }
      } else {
        const txt = dT.getData("text/plain");
        if (txt) {
          swText.current && swText.current.show(txt, evt);
        }
      }
    };

    window.addEventListener("paste", onpaste);

    return () => {
      window.removeEventListener("paste", onpaste);
    };
  }, [selected]);

  const toggleSelected = () => {
    Settings.emit('selectMediaBlock', containerRef.current)
  }

  const changeSeected = (elm) => {
    if (elm === containerRef.current){
      setSelected(true)
    } else {
      setSelected(false)
    }
  }

  useEffect(() => {
    return Settings.on('selectMediaBlock', elm => {
      changeSeected(elm)
    })
  },[])

  return (
    <div>
      <div onClick={toggleSelected} className={classes("marker",selected &&"selected")}></div>
      <div
        style={{ ...(props.containerStyle || {}) }}
        tabIndex={"-1"}
        ref={containerRef}
      >
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {blocks.map((item, index) => {
                  return (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          onClick={(event) =>
                            Settings.emit("block-press", { index, event })
                          }
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style
                          )}
                        >
                          {blocksTypes[item.type](
                            item,
                            props.scrollRef,
                            {
                              showDialog,
                              removeBlock,
                              moveBlock,
                              saveValue,
                              changeBlockType,
                            },
                            {
                              formType: props.formType,
                              disabled,
                              disableActions:
                                index === 0 &&
                                props.firstDisableAction !== undefined
                                  ? props.firstDisableAction
                                  : props.disableActions,
                              ...props.options[item.type],
                            }
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {disabled !== true && props.showAddButton === true && (
          <Button
            containerStyle={
              props.buttonContainerStyle
                ? props.buttonContainerStyle
                : { marginTop: 16 }
            }
            text={props.buttonText}
            onClick={(e) => showDialog(void 0, e)}
          />
        )}
      </div>

      <Switch
        ref={sw}
        data={[
          props.blocks.includes("header1") &&
            availableBlocks.includes("header1") && {
              title: "Заголовок 1 уровня",
              handler: (item) => addBlock(item, "header1", ""),
            },
          props.blocks.includes("header2") &&
            availableBlocks.includes("header2") && {
              title: "Заголовок 2 уровня",
              handler: (item) => addBlock(item, "header2", ""),
            },
          props.blocks.includes("text") &&
            availableBlocks.includes("text") && {
              title: "Текстовый блок",
              handler: (item) => addBlock(item, "text", ""),
            },
          props.blocks.includes("html") &&
            availableBlocks.includes("html") && {
              title: "Текстовый редактор",
              handler: (item) => addBlock(item, "html", ""),
            },
          props.blocks.includes("highlight") &&
            availableBlocks.includes("highlight") && {
              title: "Выделенный текст",
              handler: (item) => addBlock(item, "highlight", ""),
            },
          props.blocks.includes("photo") &&
            availableBlocks.includes("photo") && {
              title: "Картинка",
              handler: (item) => addBlock(item, "photo", {}),
            },
          props.blocks.includes("builtInPDF") &&
            availableBlocks.includes("builtInPDF") && {
              title: "Встроенный PDF",
              handler: (item) => addBlock(item, "builtInPDF", {}),
            },
          props.blocks.includes("video") &&
            availableBlocks.includes("video") && {
              title: "Видео",
              handler: (item) => addBlock(item, "video", {}),
            },
          props.blocks.includes("audio") &&
            availableBlocks.includes("audio") && {
              title: "Аудио",
              handler: (item) => addBlock(item, "audio", {}),
            },
        ].filter((e) => !!e)}
      />

      <Switch
        ref={sw2}
        data={[
          props.blocks.includes("photo") &&
            availableBlocks.includes("photo") && {
              title: "Картинка",
              handler: (item) => addBlock(void 0, "photo", item),
            },
          props.blocks.includes("builtInPDF") &&
            availableBlocks.includes("builtInPDF") && {
              title: "Встроенный PDF",
              handler: (item) => addBlock(void 0, "builtInPDF", item),
            },
          props.blocks.includes("video") &&
            availableBlocks.includes("video") && {
              title: "Видео",
              handler: (item) => addBlock(void 0, "video", item),
            },
          props.blocks.includes("audio") &&
            availableBlocks.includes("audio") && {
              title: "Аудио",
              handler: (item) => addBlock(void 0, "audio", item),
            },
        ].filter((e) => !!e)}
      />

      <Switch
        ref={swText}
        data={[
          props.blocks.includes("header1") &&
            availableBlocks.includes("header1") && {
              title: "Заголовок 1 уровня",
              handler: (item) => addBlock(void 0, "header1", item),
            },
          props.blocks.includes("header2") &&
            availableBlocks.includes("header2") && {
              title: "Заголовок 2 уровня",
              handler: (item) => addBlock(void 0, "header2", item),
            },
          props.blocks.includes("text") &&
            availableBlocks.includes("text") && {
              title: "Текстовый блок",
              handler: (item) => addBlock(void 0, "text", item),
            },
          props.blocks.includes("html") &&
            availableBlocks.includes("html") && {
              title: "Текстовый редактор",
              handler: (item) => addBlock(void 0, "html", item),
            },
          props.blocks.includes("highlight") &&
            availableBlocks.includes("highlight") && {
              title: "Выделенный текст",
              handler: (item) => addBlock(void 0, "highlight", item),
            },
        ].filter((e) => !!e)}
      />

      <Switch
        ref={swChange}
        data={[
          props.blocks.includes("header1") &&
            availableBlocks.includes("header1") && {
              title: "Заголовок 1 уровня",
              handler: (item) => changeType(void 0, "header1", item),
            },
          props.blocks.includes("header2") &&
            availableBlocks.includes("header2") && {
              title: "Заголовок 2 уровня",
              handler: (item) => changeType(void 0, "header2", item),
            },
          props.blocks.includes("text") &&
            availableBlocks.includes("text") && {
              title: "Текстовый блок",
              handler: (item) => changeType(void 0, "text", item),
            },
          props.blocks.includes("html") &&
            availableBlocks.includes("html") && {
              title: "Текстовый редактор",
              handler: (item) => changeType(void 0, "html", item),
            },
          props.blocks.includes("highlight") &&
            availableBlocks.includes("highlight") && {
              title: "Выделенный текст",
              handler: (item) => changeType(void 0, "highlight", item),
            },
        ].filter((e) => !!e)}
      />
    </div>
  );
});

MainLesonBlock.defaultProps = {
  blocks: [
    "photo",
    "video",
    "header1",
    "header2",
    "text",
    "highlight",
    "audio",
    "builtInPDF",
    "html",
  ],
  showAddButton: true,
  selected:true,
  options: {
    photo: {},
    video: {},
    header1: {},
    header2: {},
    text: {},
    highlight: {},
  },
};

export default MainLesonBlock;

const dynamicStyle = (Colors, Fonts) => {
  return {
    marker: {
      height: 47,
      width: 20,
    //  backgroundColor: "#f00",
      position: "absolute",
      marginLeft: -40,
      cursor: "pointer",
      border:`3px solid ${Colors.neutral2}`,
      borderRadius:16
    },
    selected:{
      border:`3px solid ${Colors.color1}`,
      backgroundColor: Colors.color1
    },
  };
};
